import { canCreateUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { createUser, getUsers } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { CreateUserCommonForm } from '@/routes/_layout/users/-components/create-user-common-form.js';
import {
  createUserCommonFormSchema,
  createUserOptionsFromFormData,
  nonCoreUserRolesFormSchema,
} from '@/routes/_layout/users/-components/form-schema.js';
import { NonCoreUserRolesForm } from '@/routes/_layout/users/-components/non-core-user-roles-form.js';
import { isFullAirAdmin } from '@airreading/common';
import { UserRoleAssignmentOptions } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

interface CreateUserProps {
  trigger?: ReactNode;
}

const airAdminRoleFlags = ['isAirAdmin', 'isAirLimitedAdmin', 'isAirLimitedAdminCoaches'] as const;

const formSchema = createUserCommonFormSchema
  .extend(nonCoreUserRolesFormSchema.shape)
  .extend({
    isAirAdmin: z.boolean().default(false),
    isAirLimitedAdmin: z.boolean().default(false),
    isAirLimitedAdminCoaches: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    const selected = airAdminRoleFlags.filter((flag) => data[flag]);
    if (selected.length <= 1) {
      return;
    }
    for (const flag of selected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A user can hold only one Air admin role',
        path: [flag],
      });
    }
  });
type Form = z.infer<typeof formSchema>;

export const CreateUser = ({ trigger }: CreateUserProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: createUser,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();
  const canGrantAdminRoles = isFullAirAdmin(actingUser);

  const onSubmit = ({ formData, closeModal, reset }: SubmitProps<Form>) => {
    try {
      const payload = createUserOptionsFromFormData(formData);

      if (canGrantAdminRoles && formData.isAirAdmin) {
        payload.assignRoles?.push({
          role: 'air_admin',
          schoolId: null,
        });
      }

      if (canGrantAdminRoles && formData.isAirLimitedAdmin) {
        payload.assignRoles?.push({
          role: 'air_limited_admin',
          schoolId: null,
        });
      }

      if (canGrantAdminRoles && formData.isAirLimitedAdminCoaches) {
        payload.assignRoles?.push({
          role: 'air_limited_admin_coaches',
          schoolId: null,
        });
      }

      if (formData.isSchoolAdmin) {
        if (!formData.adminSchools.length) {
          toast({
            title: 'Please select a school to assign school admin role',
          });
          return;
        }
        const schoolAdminRoles: UserRoleAssignmentOptions = formData.adminSchools.map(({ value }) => ({
          role: 'school_admin',
          schoolId: value,
        }));
        payload.assignRoles?.push(...schoolAdminRoles);
      }

      return mutateAsync(payload)
        .then(() => {
          toast({
            title: 'User created successfully',
          });
          closeModal();
          reset();
          queryClient.invalidateQueries(getUsers());
        })
        .catch((error) => {
          toast({
            title: airServerErrorFrom(error)?.userFacingMessage || 'Failed to create new user with unknown error',
          });
          console.error(error);
        });
    } catch (error: unknown) {
      if (error instanceof TypeError) {
        toast({ title: error.message });
        return;
      }
    }
  };

  if (!canCreateUsers(actingUser)) {
    return null;
  }

  return (
    <FormModal
      modalTitle="Create User"
      formSchema={formSchema}
      trigger={trigger || <Button>Create other user</Button>}
      dialogContentProps={{ className: 'max-w-3xl' }}
      defaultValues={{ timeZone: undefined }}
      onSubmit={onSubmit}
    >
      {({ form }) => {
        return <CreateUserForm form={form} canGrantAdminRoles={canGrantAdminRoles} />;
      }}
    </FormModal>
  );
};

function CreateUserForm({ form, canGrantAdminRoles }: { form: UseFormReturn<Form>; canGrantAdminRoles: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2 py-4">
      <CreateUserCommonForm form={form} />
      {canGrantAdminRoles && (
        <div className="col-span-2 flex flex-wrap items-start gap-8">
          <FormCheckbox form={form} name="isAirAdmin" label="Is Air Admin" />
          <FormCheckbox form={form} name="isAirLimitedAdmin" label="Is Air Limited Admin" />
          <FormCheckbox form={form} name="isAirLimitedAdminCoaches" label="Is Air Limited Admin Teachers" />
        </div>
      )}
      <NonCoreUserRolesForm form={form} />
    </div>
  );
}
